/**
 * Reflections — Phase 6A (Figma refresh).
 *
 * Three tabs: Monthly · Milestones · Journal.
 *   • Monthly: 2120-authored month summaries + user's private note per month
 *     (Phase 6B swaps fixtures for real month recap composer output).
 *   • Milestones: 2120-detected life-of-account milestones (Phase 6B wires).
 *   • Journal: reads/writes weekly_reflections table — same schema the app
 *     already uses — so nothing else has to change.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { ChevronDown, Loader2, NotebookPen } from 'lucide-react';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { formatMoney } from '@/lib/format';
import { computeMonthlyRecaps, detectMilestones } from '@/lib/insightsSignals';
import EmptyState from '@/components/EmptyState';

const TABS = [
  { id: 'monthly',    label: 'Monthly' },
  { id: 'milestones', label: 'Milestones' },
  { id: 'journal',    label: 'Journal' },
];

// ---------- Monthly (real, powered by computeMonthlyRecaps) ----------
function MonthlyTab({ recaps, baseCurrency }) {
  const [expanded, setExpanded] = useState(recaps[0]?.month || null);
  const currentMonth = new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4" data-testid="reflections-tab-monthly">
      <div className="bg-white rounded-2xl border border-border p-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">About Monthly Reflections</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Each month, 2120 prepares a summary of what happened financially — so you can see your progress, review your decisions, and add your own perspective.
        </p>
      </div>

      <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: '#163A3230' }}>
        <div className="px-6 py-5 bg-sage-light/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-evergreen/70">{currentMonth}</p>
              <p className="font-fraunces text-lg font-medium text-charcoal mt-0.5">Month in progress</p>
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-evergreen/10 text-evergreen/80">
              In progress
            </span>
          </div>
          <p className="mt-4 text-xs text-muted-foreground italic">&ldquo;Your full monthly reflection will be ready at the start of next month.&rdquo;</p>
        </div>
      </div>

      {recaps.length === 0 && (
        <div className="bg-white rounded-2xl border border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">Once you have at least two months of transactions, monthly recaps will appear here.</p>
        </div>
      )}

      {recaps.map((r) => {
        const isOpen = expanded === r.month;
        return (
          <div key={r.month} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`monthly-reflection-${r.month}`}>
            <button className="w-full text-left px-6 py-5 flex items-center gap-4" onClick={() => setExpanded(isOpen ? null : r.month)}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-charcoal">{r.month} {r.year}</p>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full num"
                    style={{
                      backgroundColor: r.score >= 75 ? '#EEF4F0' : r.score >= 65 ? '#FFF8EE' : '#FAF1EE',
                      color:           r.score >= 75 ? '#3D7A63' : r.score >= 65 ? '#B07210' : '#A84E38',
                    }}>
                    Score {r.score}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{r.headline}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-charcoal num">{formatMoney(r.surplus, baseCurrency)}</p>
                <p className="text-xs font-medium mt-0.5 num" style={{ color: r.vs_last >= 0 ? '#3D7A63' : '#A84E38' }}>
                  {r.vs_last >= 0 ? '↑' : '↓'} {Math.abs(r.vs_last)}%
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && (
              <div className="px-6 pb-6 border-t border-border/60 pt-5 space-y-5">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60 mb-3">What happened</p>
                  <ul className="space-y-2">
                    {r.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-charcoal/80 leading-relaxed">
                        <span className="w-1 h-1 rounded-full bg-evergreen mt-2 shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
                {r.note ? (
                  <div className="rounded-xl p-4 bg-offwhite/60" style={{ borderLeft: '3px solid #A8C3B080' }}>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground/60 mb-2">Your note</p>
                    <p className="text-sm text-charcoal/70 italic leading-relaxed">&ldquo;{r.note}&rdquo;</p>
                  </div>
                ) : (
                  // TODO(Phase 6C follow-up): Personal notes per month need
                  // a schema/persistence decision (Supabase table vs
                  // localStorage). Hidden for now — the dead button was
                  // misleading users into thinking it worked. Reintroduce
                  // once notes have a real store.
                  null
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------- Milestones (real, powered by detectMilestones) ----------
function MilestonesTab({ milestones }) {
  return (
    <div className="space-y-4" data-testid="reflections-tab-milestones">
      <div className="bg-white rounded-2xl border border-border p-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">Your Financial Story</p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          These are the moments that shaped your financial picture — goals reached, habits formed, and decisions made.
        </p>
      </div>
      {milestones.length === 0 ? (
        <div className="bg-white rounded-2xl border border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No milestones detected yet. As you contribute to goals and keep tracking, they&apos;ll appear here.</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute top-0 bottom-0 w-px bg-border" style={{ left: 20 }} />
          <div className="space-y-3">
            {milestones.map((m) => (
              <div key={m.id} className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 z-10" style={{ backgroundColor: m.bg, boxShadow: '0 0 0 3px white' }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: m.color }} />
                </div>
                <div className="flex-1 bg-white rounded-2xl border border-border p-4 -mt-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: m.color }}>{m.label}</span>
                    <span className="text-[10px] text-muted-foreground">{m.date}</span>
                  </div>
                  <p className="text-sm font-semibold text-charcoal leading-snug">{m.headline}</p>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{m.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Journal (reads/writes weekly_reflections) ----------
const PROMPTS = [
  'How would you describe your relationship with money this month?',
  'What is one financial decision you feel good about?',
  'Is there anything that felt harder to manage?',
  'What would you do differently next month?',
];

function JournalTab() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [writeMode, setWriteMode] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const prompt = useMemo(() => PROMPTS[Math.floor(Math.random() * PROMPTS.length)], []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('weekly_reflections')
        .select('id, week_ending, answer_text, updated_at, question_id')
        .order('week_ending', { ascending: false })
        .limit(20);
      setEntries(data || []);
    } catch (e) { console.warn('[journal] load failed:', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (draft.trim().length < 10) { toast.error('A little more, please — at least 10 characters.'); return; }
    setSaving(true);
    try {
      const today = new Date();
      const wk = new Date(today); wk.setDate(today.getDate() - today.getDay());
      const week_ending = wk.toISOString().slice(0, 10);
      const { data: { user } } = await supabase.auth.getUser();
      const row = { user_id: user?.id, week_ending, answer_text: draft.trim(), question_id: null };
      const { error } = await supabase.from('weekly_reflections').upsert(row, { onConflict: 'user_id,week_ending' });
      if (error) throw error;
      toast.success('Saved.');
      setDraft(''); setWriteMode(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Could not save reflection.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="reflections-tab-journal">
      <div className="bg-white rounded-2xl border border-border p-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-3">A prompt for you</p>
        <p className="font-fraunces text-lg font-medium text-charcoal leading-snug mb-4">&ldquo;{prompt}&rdquo;</p>
        {!writeMode ? (
          <button onClick={() => setWriteMode(true)} className="px-5 py-2.5 rounded-xl text-sm font-medium bg-evergreen text-white hover:opacity-90 transition-opacity" data-testid="journal-write-cta">
            Write reflection
          </button>
        ) : (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write your thoughts here…"
              className="w-full rounded-xl border border-border bg-offwhite px-4 py-3 text-sm text-charcoal resize-none focus:outline-none focus:ring-2 focus:ring-evergreen/20 leading-relaxed"
              rows={5}
              autoFocus
              data-testid="journal-textarea"
            />
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => { setWriteMode(false); setDraft(''); }} className="text-sm text-muted-foreground hover:text-charcoal transition-colors">
                Cancel
              </button>
              <button onClick={save} disabled={saving || draft.trim().length < 10}
                className="px-5 py-2 rounded-xl text-sm font-medium bg-evergreen text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity flex items-center gap-1.5"
                data-testid="journal-save-button">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save reflection
              </button>
            </div>
          </>
        )}
      </div>

      {loading ? (
        <div className="text-center text-xs text-muted-foreground py-8">Loading past reflections…</div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={NotebookPen}
          title="Start your first reflection"
          body="A weekly journal about your money — what you learned, what surprised you, what you'd change. 2120 asks one question a week; you answer in your own words."
          primaryCta={{
            label: 'Answer this week\u2019s question',
            onClick: () => {
              const el = document.querySelector('[data-testid="reflection-answer-input"], textarea');
              if (el) el.focus();
            },
            testid: 'reflections-empty-primary',
          }}
          testid="reflections-empty-state"
        />
      ) : (
        entries.map((entry) => (
          <div key={entry.id} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`journal-entry-${entry.id}`}>
            <div className="px-6 py-4 border-b border-border/50">
              <p className="text-sm font-semibold text-charcoal">Week ending {new Date(entry.week_ending).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-charcoal/80 leading-relaxed whitespace-pre-wrap">{entry.answer_text}</p>
            </div>
          </div>
        ))
      )}

      <div className="rounded-2xl p-5 bg-offwhite">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Reflections are private and stored only for you. 2120 does not use your journal entries to make financial recommendations.
        </p>
      </div>
    </div>
  );
}

// ---------- Page ----------
export default function Reflections() {
  usePageTitle('Reflections');
  const { profile } = useAuth();
  const baseCurrency = profile?.base_currency || profile?.currency || 'INR';
  const [tab, setTab] = useState('monthly');

  // Phase 6C — Load real transactions + goals for computed recaps & milestones.
  // Uses the authoritative `txn_date` column (schema.sql).
  const [txns, setTxns] = useState([]);
  const [goals, setGoals] = useState([]);
  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const start = new Date(); start.setMonth(start.getMonth() - 12); start.setDate(1);
        const startISO = start.toISOString().slice(0, 10);
        const [txnR, goalR] = await Promise.all([
          supabase.from('transactions')
            .select('id, txn_date, amount, category, type, description')
            .gte('txn_date', startISO)
            // Phase 84.9 — fetch NEWEST-first + limit 5000.
            // With ascending order the query returned the OLDEST 5000 txns,
            // which for heavy-import users (>5000 txns / 12 months) silently
            // dropped the most recent months — the exact symptom of "June is
            // missing from Reflections" in production. Descending guarantees
            // the latest complete months are always represented.
            .order('txn_date', { ascending: false })
            .limit(5000),
          supabase.from('goals').select('id, title, current_amount, target_amount, status, updated_at'),
        ]);
        if (!c) { setTxns(txnR.data || []); setGoals(goalR.data || []); }
      } catch (e) { console.warn('[reflections] load failed:', e); }
    })();
    return () => { c = true; };
  }, []);
  const recaps     = useMemo(() => computeMonthlyRecaps({ txns, goals, baseCurrency }), [txns, goals, baseCurrency]);
  const milestones = useMemo(() => detectMilestones({ txns, goals, baseCurrency }),      [txns, goals, baseCurrency]);

  return (
    <div className="min-h-full" data-testid="reflections-page">
      <div className="px-6 lg:px-10 pt-8 pb-6 border-b border-border bg-white">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-1">Awareness</p>
        <h1 className="font-fraunces text-2xl lg:text-3xl font-medium text-charcoal tracking-tight">Reflections</h1>
        <p className="text-sm text-muted-foreground mt-1">Your financial story — month by month.</p>
        <div className="flex items-stretch gap-0 mt-6 -mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`reflections-tab-${t.id}`}
              className={`px-4 py-3 text-sm font-medium relative transition-colors shrink-0 whitespace-nowrap ${tab === t.id ? 'text-evergreen' : 'text-muted-foreground'}`}>
              {t.label}
              {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-evergreen rounded-t-sm" />}
            </button>
          ))}
        </div>
      </div>
      <div className="px-6 lg:px-10 py-8 max-w-2xl">
        {tab === 'monthly'    && <MonthlyTab recaps={recaps} baseCurrency={baseCurrency} />}
        {tab === 'milestones' && <MilestonesTab milestones={milestones} />}
        {tab === 'journal'    && <JournalTab />}
      </div>
    </div>
  );
}
